import { useCallback, useEffect, useMemo } from 'react';
import type { IObservableValue } from 'mobx';
import { observable, runInAction } from 'mobx';

import type { GetUserQuestionAnswersRankingResponse } from '@genially/contracts';

export enum PositionChange {
  UP = 'up',
  DOWN = 'down',
  SAME = 'same',
}

interface UseCalculateRankingPositionChangesProps {
  currentData: GetUserQuestionAnswersRankingResponse;
}

const calculatePositionChanges = (
  currentData: GetUserQuestionAnswersRankingResponse,
  previousData?: GetUserQuestionAnswersRankingResponse,
): Map<string, PositionChange> => {
  if (!previousData) {
    return new Map(currentData.map(user => [user.userAlias, PositionChange.SAME]));
  }

  const getPositionMap = (
    data: GetUserQuestionAnswersRankingResponse,
  ): Record<string, number> =>
    data.reduce((acc, user, index) => ({ ...acc, [user.userAlias]: index }), {});

  const previousPositionMap = getPositionMap(previousData);
  const currentPositionMap = getPositionMap(currentData);

  const positionChanges = new Map<string, PositionChange>();

  currentData.forEach(user => {
    const { userAlias } = user;
    const previousPosition = previousPositionMap[userAlias];
    const currentPosition = currentPositionMap[userAlias];

    let positionChange: PositionChange;
    if (previousPosition < currentPosition) {
      positionChange = PositionChange.DOWN;
    } else if (previousPosition > currentPosition) {
      positionChange = PositionChange.UP;
    } else {
      positionChange = PositionChange.SAME;
    }

    positionChanges.set(userAlias, positionChange);
  });

  return positionChanges;
};

const observablePreviousData: IObservableValue<
  GetUserQuestionAnswersRankingResponse | undefined
> = observable.box(undefined);

export const useCalculateRankingPositionChanges = ({
  currentData,
}: UseCalculateRankingPositionChangesProps) => {
  const resetCalculations = useCallback(() => {
    runInAction(() => {
      observablePreviousData.set(currentData);
    });
  }, [currentData]);

  useEffect(() => {
    const previousData = observablePreviousData.get();
    if (!previousData || previousData?.length === 0) {
      runInAction(() => {
        observablePreviousData.set(currentData);
      });
    }
  }, [currentData]);

  const rankingChanges = useMemo(
    () => calculatePositionChanges(currentData, observablePreviousData.get()),
    [currentData],
  );

  return { rankingChanges, resetCalculations };
};
